import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useContextManagementState, useProjectState } from '../../hooks/useProjectState';
import { projectService } from '../../services/projectService';
import { projectStateService } from '../../services/projectStateService';
import { ProjectItem, ProjectStatus } from '../../types/project';

import { ContextManagementScreen } from './ContextManagementScreen';

interface ContextManagementParams extends Record<string, string | undefined> {
  projectId: string;
}

const STATUS_ADVANCE_ORDER: string[] = [
  'context_upload',
  'context_review',
  'generation',
  'in_review',
  'completed',
];

/**
 * Returns true when advancing to `target` is a forward move from `current`.
 */
const isStatusAdvancement = (current: string, target: string): boolean =>
  STATUS_ADVANCE_ORDER.indexOf(target) > STATUS_ADVANCE_ORDER.indexOf(current);

/**
 * Context Management Page wrapper component
 * Loads project data and provides navigation handlers for the Context Management Screen
 */
export const ContextManagementPage: React.FC = () => {
  const { projectId } = useParams<ContextManagementParams>();
  const navigate = useNavigate();
  
  const [project, setProject] = useState<ProjectItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const projectIdNum = projectId ? Number.parseInt(projectId, 10) : 0;

  // Use context state management
  const { 
    state: contextState, 
    updateState: updateContextState, 
    saveState: saveContextState,
    isLoading: contextLoading, 
  } = useContextManagementState(projectIdNum, { 
    autoSave: true,
    autoSaveDelay: 2000, 
  });

  const { updateProjectStatus } = useProjectState();

  // Load project data
  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) {
        setError('Project ID is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Try to load from state service first
        try {
          const projectState = await projectStateService.getProjectState(projectIdNum);
          setProject(projectState.project);
          setError(null);
        } catch (stateError) {
          // If not in state, load from API and initialize state
          const projectData = await projectService.getProject(projectIdNum);
          setProject(projectData);
          
          // Initialize state for this project
          await projectStateService.initializeProjectState(projectData);
          setError(null);
        }
      } catch (err) {
        setError('Failed to load project data');
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [projectId, projectIdNum]);

  // Handle project updates
  const handleProjectUpdate = async (updatedProject: ProjectItem) => {
    try {
      // For now, we'll just update the local state
      // In a real implementation, you'd call an API to save the project changes
      setProject(updatedProject);
      
      // If title changed, use the existing updateProjectTitle method
      if (project && updatedProject.title !== project.title) {
        await projectService.updateProjectTitle(updatedProject.id, updatedProject.title);
      }
    } catch (err) {
      // You could show a toast notification here
    }
  };

  // Handle stage navigation with unsaved changes check
  const handleNavigateToStage = (stage: number) => {
    if (!projectId) {return;}

    // Advance status to CONTEXT_REVIEW when the user proceeds to CRA or beyond,
    // but only if the project hasn't already reached that status or further.
    if (stage >= 3 && project) {
      const currentStatus = project.status ?? 'context_upload';
      if (isStatusAdvancement(currentStatus, 'context_review')) {
        projectStateService
          .updateProjectStatus(projectIdNum, ProjectStatus.CONTEXT_REVIEW)
          .catch(() => {/* non-critical — navigation still proceeds */});
      }
    }

    const routes = {
      1: `/project/${projectId}/setup`,        // Project Setup
      2: `/project/${projectId}/context`,      // Context Management
      3: `/project/${projectId}/cra`,          // Context Review
      4: `/project/${projectId}/adrs`,         // ADRs
      5: `/project/${projectId}/generation`,   // Architecture Generation
      6: `/project/${projectId}/c4`,           // Bizzdesign Integration / C4 Modeling
      7: `/project/${projectId}/review`,       // Final Review
    };

    const route = routes[stage as keyof typeof routes];
    if (route) {
      navigate(route);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0097a9] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Project not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-[#0097a9] text-white rounded-lg hover:bg-[#007a8a] transition-colors"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  // Render the Context Management Screen
  return (
    <ContextManagementScreen
      project={project}
      onProjectUpdate={handleProjectUpdate}
      onNavigateToStage={handleNavigateToStage}
      contextState={contextState || undefined}
      onUpdateContextState={updateContextState}
    />
  );
};
